import React from "react";
import { Grid, Paper, Typography } from "@mui/material";
import { DocumentType } from "constants/adminApi";
import { VerificationStepSummary } from "types/admin";
import AdminKycDocumentPreviewCard from "./AdminKycDocumentPreviewCard";
import { KycDocumentLoadState } from "../hooks/useKycDocumentLoader";
import { KycDetailTag } from "./kycDocumentDetailTags";

export interface KycDocumentPreviewConfig {
  documentType: DocumentType;
  title: string;
  subtitle?: string;
  objectKey?: string;
  verificationStep?: VerificationStepSummary | null;
  detailTags?: KycDetailTag[];
  nameMatched?: boolean | null;
  onExplainNameMatch?: () => void;
  explainNameMatchDisabled?: boolean;
  explainNameMatchDisabledReason?: string;
  state: KycDocumentLoadState;
  downloading?: boolean;
}

interface AdminKycDocumentsPanelProps {
  documents: [KycDocumentPreviewConfig, KycDocumentPreviewConfig];
  onExpand: (documentType: DocumentType, objectKey: string) => void;
  onDownload: (documentType: DocumentType, objectKey: string) => void;
  onRetry: (documentType: DocumentType, objectKey: string) => void;
}

function AdminKycDocumentsPanel({
  documents,
  onExpand,
  onDownload,
  onRetry,
}: AdminKycDocumentsPanelProps) {
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" color="secondary.main" sx={{ mb: 0.5 }}>
        KYC Documents
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Compare PAN and bank proofs side by side. Key details appear as tags above each
        preview. Click a preview to expand, or use the toolbar to download.
      </Typography>
      <Grid container spacing={2.5}>
        {documents.map((doc) => (
          <Grid item xs={12} md={6} key={doc.documentType}>
            <AdminKycDocumentPreviewCard
              title={doc.title}
              subtitle={doc.subtitle}
              documentType={doc.documentType}
              objectKey={doc.objectKey}
              verificationStep={doc.verificationStep}
              detailTags={doc.detailTags}
              nameMatched={doc.nameMatched}
              onExplainNameMatch={doc.onExplainNameMatch}
              explainNameMatchDisabled={doc.explainNameMatchDisabled}
              explainNameMatchDisabledReason={doc.explainNameMatchDisabledReason}
              state={doc.state}
              downloading={doc.downloading}
              onExpand={() => {
                if (doc.objectKey) {
                  onExpand(doc.documentType, doc.objectKey);
                }
              }}
              onDownload={() => {
                if (doc.objectKey) {
                  onDownload(doc.documentType, doc.objectKey);
                }
              }}
              onRetry={() => {
                if (doc.objectKey) {
                  onRetry(doc.documentType, doc.objectKey);
                }
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

export default AdminKycDocumentsPanel;
